/**
 * Email Collector Section
 * Handles form submission and Shopify newsletter integration
 * Features: Form validation, success/error handling, accessibility
 */

class EmailCollector {
  constructor() {
    this.sections = document.querySelectorAll('.email-collector');
    
    if (this.sections.length > 0) {
      this.init();
    }
  }

  init() {
    this.sections.forEach(section => {
      this.initForm(section);
    });
  }

  /**
   * Initialize form functionality
   */
  initForm(section) {
    const form = section.querySelector('.email-collector__form');
    const emailInput = section.querySelector('.email-collector__input');
    const submitButton = section.querySelector('.email-collector__button');
    const successMessage = section.querySelector('.email-collector__message--success');
    const errorMessage = section.querySelector('.email-collector__message--error');
    
    if (!form || !emailInput || !submitButton) return;
    
    // Form submission handler
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const email = emailInput.value.trim();
      
      if (!this.validateEmail(email)) {
        this.showError(errorMessage, 'Please enter a valid email address.');
        return;
      }
      
      // Show loading state
      this.setLoadingState(submitButton, true);
      this.hideMessages(successMessage, errorMessage);
      
      try {
        // Submit to Shopify
        const response = await this.submitToShopify(email);
        
        if (response.ok) {
          this.showSuccess(successMessage, 'Thank you! You\'ve been subscribed to our newsletter.');
          emailInput.value = '';
          
          // Track conversion (if analytics is available)
          this.trackConversion(email);
        } else {
          throw new Error('Subscription failed');
        }
      } catch (error) {
        console.error('Email subscription error:', error);
        this.showError(errorMessage, 'Something went wrong. Please try again.');
      } finally {
        this.setLoadingState(submitButton, false);
      }
    });
    
    // Real-time email validation
    emailInput.addEventListener('input', () => {
      this.hideMessages(successMessage, errorMessage);
    });
    
    // Enter key handling
    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        form.dispatchEvent(new Event('submit'));
      }
    });
  }

  /**
   * Validate email format
   */
  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Submit email to Shopify newsletter
   */
  async submitToShopify(email) {
    const formData = new FormData();
    formData.append('form_type', 'customer');
    formData.append('utf8', '✓');
    formData.append('contact[email]', email);
    formData.append('contact[tags]', 'newsletter');
    
    return fetch('/contact#contact_form', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
  }

  /**
   * Show success message
   */
  showSuccess(messageElement, text) {
    if (!messageElement) return;
    
    messageElement.querySelector('p').textContent = text;
    messageElement.style.display = 'block';
    messageElement.setAttribute('aria-live', 'polite');
    
    // Scroll to message
    messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Show error message
   */
  showError(messageElement, text) {
    if (!messageElement) return;
    
    messageElement.querySelector('p').textContent = text;
    messageElement.style.display = 'block';
    messageElement.setAttribute('aria-live', 'assertive');
    
    // Scroll to message
    messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Hide all messages
   */
  hideMessages(successMessage, errorMessage) {
    if (successMessage) {
      successMessage.style.display = 'none';
    }
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }
  }

  /**
   * Set loading state for button
   */
  setLoadingState(button, isLoading) {
    if (!button) return;
    
    if (isLoading) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Subscribing...';
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = button.dataset.originalText || 'Join the movement';
    }
  }

  /**
   * Track conversion for analytics
   */
  trackConversion(email) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'newsletter_signup', {
        event_category: 'engagement',
        event_label: 'email_collector',
        value: 1
      });
    }
    
    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead', {
        content_name: 'Newsletter Signup',
        content_category: 'Email Collector'
      });
    }
    
    // Custom event for other tracking
    window.dispatchEvent(new CustomEvent('emailCollectorSignup', {
      detail: { email: email }
    }));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EmailCollector();
  });
} else {
  new EmailCollector();
}

// Reinitialize on Shopify section events (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new EmailCollector();
  });
  
  document.addEventListener('shopify:section:unload', () => {
    // Clean up if needed
  });
}

// Export for external use
window.EmailCollector = EmailCollector;
